import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Button,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { DialogButtonsBox } from "../Components/DialogButtons";
import { useTranslation } from "react-i18next";

interface IDialogButton {
  label: string;
  clickCallback?: VoidFunction;
}

export type TDialogData = { [Key: string]: string };
interface IDialogContent {
  title: string;
  text: string;
  buttons?: IDialogButton[];
}

type TDialogTrigger = (dialogContent: IDialogContent) => void;
const DialogContext = React.createContext<TDialogTrigger>(() => {
  console.error("Unsupported method. Default DialogContext shouldn't be used!");
});

export const DialogProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [buttons, setButtons] = React.useState<IDialogButton[]>([]);
  const { t } = useTranslation();

  const showDialog = (dialogContent: IDialogContent) => {
    setTitle(dialogContent.title);
    setText(dialogContent.text);
    setButtons(
      dialogContent.buttons || [
        {
          label: t("Close"),
        },
      ]
    );
    setOpen(true);
  };

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setOpen(false);
  };

  const handleCallback = (
    dialogButton: IDialogButton,
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    dialogButton.clickCallback?.();
    handleClose(event, reason);
  };

  return (
    <DialogContext.Provider value={showDialog}>
      <Dialog open={open} onClose={handleClose} data-cy="dialog">
        <DialogTitle data-cy="dialog-title">{title}</DialogTitle>
        <DialogContent>
          <DialogContentText data-cy="dialog-content">{text}</DialogContentText>
        </DialogContent>
        <DialogButtonsBox>
          {buttons.map((button: IDialogButton, index: number) => (
            <Button
              key={button.label}
              onClick={(params) => {
                handleCallback(button, params);
              }}
              data-cy={`dialog-button-${index}`}
            >
              {button.label}
            </Button>
          ))}
        </DialogButtonsBox>
      </Dialog>
      {children}
    </DialogContext.Provider>
  );
};

const useDialog = () => {
  return React.useContext(DialogContext);
};

export default useDialog;
